import { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { AIEmojisAPI } from '@makyoapp/makyo-sdk'
import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter, TableAdapter } from '@makyoapp/makyo-common'
import {
  Checkbox16,
  Date16,
  DateRange16,
  Dropdown16,
  Numtrack16,
  Radio16,
  Segmented16,
  SliderArrow16,
  SliderRange16,
  Toggle16,
  Trash16,
} from '@makyoapp/makyo-icon/icon'
import { Field, MaterialIconPicker, SelectDropdown, SimpleInput, SingleIconSelect, Switch } from '@makyoapp/makyo-ui'

import { UserColumnFilterType, UserColumnFilterValue } from '@/shared/builder/types'
import { SHOW_SEARCH_LIMIT } from '@/shared/constants'

import { Accordion, AccordionItem } from '../Accordion/Accordion'
import { RenderColumn } from '../RenderColumn'
import { RepeaterButton } from '../RepeaterButton'

const filterTypeIconMap = {
  [UserColumnFilterType.TextSelection]: <Dropdown16 />,
  [UserColumnFilterType.TextSegmented]: <Segmented16 />,
  [UserColumnFilterType.TextCheckbox]: <Checkbox16 />,
  [UserColumnFilterType.TextRadio]: <Radio16 />,
  [UserColumnFilterType.NumberSelection]: <Dropdown16 />,
  [UserColumnFilterType.NumberExactAmount]: <Numtrack16 />,
  [UserColumnFilterType.NumberRange]: <SliderRange16 />,
  [UserColumnFilterType.DateExactDate]: <Date16 />,
  [UserColumnFilterType.DateDateRange]: <DateRange16 />,
  [UserColumnFilterType.RatingAmountWithin]: <SliderArrow16 />,
  [UserColumnFilterType.RatingAtLeast]: <Segmented16 />,
  [UserColumnFilterType.BooleanToggle]: <Toggle16 />,
  [UserColumnFilterType.ContentNotEmpty]: <Toggle16 />,
}

function getUserFilterDefaultIcon(col: ColumnAdapter): string {
  switch (col.type) {
    case ColumnTypes.Text:
      return 'dns'
    case ColumnTypes.URL:
      return 'link'
    case ColumnTypes.Email:
      return 'mail'
    case ColumnTypes.Phone:
      return 'phone_iphone'
    case ColumnTypes.RichText:
      return 'description'
    case ColumnTypes.RowID:
      return 'badge'
    case ColumnTypes.Emoji:
      return 'mood'
    case ColumnTypes.Icon:
      return 'category'
    case ColumnTypes.QRCode:
      return 'qr_code_scanner'
    case ColumnTypes.Barcode:
      return 'barcode_scanner'
    case ColumnTypes.Slug:
      return 'title'
    case ColumnTypes.Color:
      return 'brush'
    case ColumnTypes.Address:
      return 'home'
    case ColumnTypes.AISEO:
      return 'title'
    case ColumnTypes.AITextProcessing:
      return 'title'
    case ColumnTypes.Number:
      return 'tag'
    case ColumnTypes.Rating:
      return 'star'
    case ColumnTypes.Boolean:
      return 'toggle_on'
    case ColumnTypes.DateTime:
      return 'calendar_month'
    case ColumnTypes.MultiSelect:
      return 'list'
    case ColumnTypes.Array:
      return 'list'
    case ColumnTypes.ImageArray:
      return 'list'
    case ColumnTypes.Image:
      return 'image'
    case ColumnTypes.Attachment:
      return 'attach_file'
    case ColumnTypes.Coordinate:
      return 'location_on'
    case ColumnTypes.Function:
      return 'function'
    case ColumnTypes.Connection:
      return 'hub'
    default:
      return 'dns'
  }
}

function getFilterTypeLabel(type: UserColumnFilterType) {
  if (type === UserColumnFilterType.RatingAmountWithin) {
    return 'Slider'
  }
  if (type === UserColumnFilterType.RatingAtLeast) {
    return 'Segmented'
  }

  switch (type) {
    default:
      return type
        .split('-')
        .slice(1)
        .map((p) => {
          return p.charAt(0).toUpperCase() + p.slice(1)
        })
        .join(' ')
  }
}

function getFilterOptions(type: ColumnTypes) {
  switch (type) {
    case ColumnTypes.Number:
      return [
        UserColumnFilterType.NumberSelection,
        UserColumnFilterType.NumberExactAmount,
        UserColumnFilterType.NumberRange,
      ]
    case ColumnTypes.Rating:
      return [UserColumnFilterType.RatingAmountWithin, UserColumnFilterType.RatingAtLeast]
    case ColumnTypes.Boolean:
      return [UserColumnFilterType.BooleanToggle]
    case ColumnTypes.DateTime:
      return [UserColumnFilterType.DateExactDate, UserColumnFilterType.DateDateRange]
    case ColumnTypes.Attachment:
    case ColumnTypes.Image:
    case ColumnTypes.URL:
    case ColumnTypes.Phone:
    case ColumnTypes.Email:
      return [UserColumnFilterType.ContentNotEmpty]
    default:
      return [UserColumnFilterType.TextSelection, UserColumnFilterType.TextSegmented, UserColumnFilterType.TextRadio]
  }
}

function isMultiple(type: UserColumnFilterType) {
  return [
    UserColumnFilterType.NumberSelection,
    UserColumnFilterType.TextSelection,
    UserColumnFilterType.TextSegmented,
    UserColumnFilterType.TextRadio,
  ].includes(type)
}

//--------------------------------------------------------------------------------------//
//                                    ConditionItem                                     //
//--------------------------------------------------------------------------------------//

interface ConditionItem {
  index: number
  id?: string
  value: UserColumnFilterValue[]
  colOptions: ColumnAdapter[]
  table: TableAdapter
  onRemove: (idx: number) => void
  onChange: (idx: number, cb: (next: UserColumnFilterValue) => void) => void
}

function ConditionItem({ index, id, onChange, onRemove, value, colOptions, table }: ConditionItem) {
  const { t } = useTranslation()
  const [show, setShow] = useBoolean(false)
  const [isHover, setIsHover] = useBoolean(false)

  const col = useMemo(() => {
    return colOptions.find(({ id }) => id === value[index].column)
  }, [colOptions, value, index])

  const columnType = col?.type || ColumnTypes.Text
  const options = getFilterOptions(columnType)

  const [tempLabel, setTempLabel] = useState(value[index].label || '')

  useEffect(() => {
    setTempLabel(value[index].label || '')
  }, [id, index, setTempLabel, value])

  const allowMultiple = useMemo(() => {
    return value[index].filterType && isMultiple(value[index].filterType)
  }, [index, value])

  return (
    <AccordionItem
      itemId={index.toString()}
      className={clsx(index === value.length - 1 && 'rounded-b-md')}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      title={
        <div className='flex-c w-full justify-between'>
          <div>
            <Trans>Filter</Trans>: {col?.name}
          </div>
          {isHover && (
            <span
              role='button'
              className='w-8 h-8 flex-cc'
              onClick={(e) => {
                e.stopPropagation()
                onRemove(index)
              }}
            >
              <Trash16 />
            </span>
          )}
        </div>
      }
    >
      <div className='flex flex-col'>
        <Field>
          <label className='w-1/2'>
            <Trans>Filter By</Trans>
          </label>
          <SelectDropdown
            id={id ? `${id}-col` : undefined}
            withSearch={colOptions.length > SHOW_SEARCH_LIMIT}
            value={col}
            toggleClassName='dark:hover:bg-slate-800!'
            onChange={(opt) => {
              onChange(index, (prev) => {
                const isSame = prev.column === opt.id
                prev.column = opt.id
                if (!isSame) {
                  const newType = getFilterOptions(opt.type)[0]
                  prev.filterType = newType
                  prev.multiple = isMultiple(newType)
                  prev.icon = getUserFilterDefaultIcon(opt)
                }
              })
            }}
            menuContainerClassName='min-w-[200px]'
            optionLabel={(opt) => `${opt?.id} ${opt?.name}`}
            renderOption={(opt) => <RenderColumn withStyle={false} col={opt} />}
            options={colOptions}
          />
        </Field>

        {columnType && col && (
          <Field>
            <label className='flex-c w-1/2'>
              <Trans>Type</Trans>
            </label>
            <SingleIconSelect
              options={Object.entries(options).map(([k, v]) => {
                const title = getFilterTypeLabel(v)
                return {
                  id: v,
                  title: t(title),
                  icon: filterTypeIconMap[v],
                }
              })}
              selected={value[index].filterType}
              onClick={(opt) => {
                onChange(index, (prev) => {
                  prev.filterType = opt.id as UserColumnFilterType
                })
              }}
            />
          </Field>
        )}

        {allowMultiple && (
          <Field>
            <label className='flex-c w-1/2'>
              <Trans>Allow Multiple</Trans>
            </label>
            <Switch
              id={id ? `${id}-multi-${index}` : undefined}
              checked={value[index].multiple}
              onClick={(isChecked) => {
                if (col) {
                  onChange(index, (prev) => {
                    prev.multiple = isChecked
                  })
                }
              }}
            />
          </Field>
        )}

        <Field className='justify-between'>
          <label className='w-1/2'>
            <Trans>Icon</Trans>
          </label>
          <MaterialIconPicker
            id={id ? `${id}-icon` : undefined}
            show={show}
            onClose={() => setShow(false)}
            toggleClassName='flex-cc rounded-sm bg-zinc-200 dark:bg-slate-600 w-9 h-9'
            toggle={
              value[index].icon === '' || value[index].icon === undefined
                ? (active, toggleProps) => {
                    return <div {...toggleProps}>+</div>
                  }
                : undefined
            }
            onToggle={() => setShow(true)}
            settings={{
              opsz: 24,
              fill: 0,
              wght: 400,
              grade: 0,
            }}
            searchMs={300}
            searchFilter={async (keyword) => {
              const aiSearch = await AIEmojisAPI.ReqFindEmoji(keyword)
              return aiSearch
            }}
            value={value[index].icon ?? ''}
            clickAsDone
            onClear={() => {
              onChange(index, (prev) => {
                prev.icon = undefined
              })
            }}
            onDone={(icon) => {
              onChange(index, (prev) => {
                prev.icon = icon
              })
            }}
          />
        </Field>

        <Field>
          <label className='w-1/2'>
            <Trans>Custom Label</Trans>
          </label>
          <SimpleInput
            className='user-filter-input'
            value={tempLabel}
            onChange={(e) => {
              const val = e.currentTarget.value
              setTempLabel(val)
            }}
            onBlur={() => {
              onChange(index, (prev) => {
                prev.label = tempLabel.trim()
              })
            }}
          />
        </Field>

        {/* <Field>
          <label className='w-1/2'>
            <Trans>Show on Page</Trans>
          </label>
          <Switch
            id={id ? `${id}-page` : undefined}
            checked={value[index].showOnPage}
            onClick={(isChecked) => {
              onChange(index, (prev) => {
                prev.showOnPage = isChecked
              })
            }}
          />
        </Field> */}

        {/* <Field>
          <label className='w-1/2'>
            <Trans>Show on Filter Page</Trans>
          </label>
          <Switch
            id={id ? `${id}-filter` : undefined}
            checked={value[index].showOnFilter}
            onClick={(isChecked) => {
              onChange(index, (prev) => {
                prev.showOnFilter = isChecked
              })
            }}
          />
        </Field> */}
      </div>
    </AccordionItem>
  )
}

//--------------------------------------------------------------------------------------//
//                             UserColumnFilterRepeater                                 //
//--------------------------------------------------------------------------------------//

interface UserColumnFilterRepeaterProps {
  id?: string
  value: UserColumnFilterValue[]
  onChange: (next: UserColumnFilterValue[]) => void
  colOptions: ColumnAdapter[]
  table: TableAdapter
}

export function UserColumnFilterRepeater({ id, value, onChange, colOptions, table }: UserColumnFilterRepeaterProps) {
  const handleChange = useCallback(
    (idx: number, cb: (next: UserColumnFilterValue) => void) => {
      const nextValue = [...value]
      cb(nextValue[idx])
      onChange(nextValue)
    },
    [onChange, value]
  )

  const handleRemove = useCallback(
    (idx: number) => {
      const nextValue = [...value]
      nextValue.splice(idx, 1)
      onChange(nextValue)
    },
    [onChange, value]
  )

  return (
    <div className='flex flex-col gap-4 w-full'>
      <div className={clsx(value.length > 0 && 'border-t border-x bdr rounded-md bg-zinc-50 dark:bg-slate-700')}>
        <Accordion openMultiple>
          {value.map((v, i) => {
            return (
              <ConditionItem
                index={i}
                key={i}
                id={id}
                value={value}
                colOptions={colOptions}
                table={table}
                onChange={handleChange}
                onRemove={handleRemove}
              />
            )
          })}
        </Accordion>
      </div>
      {colOptions.length < 1 && (
        <div className='text-m-sm text-light'>
          <Trans>Please select table in data source settings</Trans>
        </div>
      )}
      <RepeaterButton
        id={id ? `${id}-repeat` : undefined}
        disabled={colOptions.length < 1}
        onClick={() => {
          if (colOptions.length > 0) {
            const nextCond = [
              ...value,
              {
                column: colOptions[0]?.id ?? '',
                filterType: getFilterOptions(colOptions[0].type)[0],
                icon: getUserFilterDefaultIcon(colOptions[0]),
                multiple: false,
                showOnPage: true,
                showOnFilter: true,
              } as UserColumnFilterValue,
            ]
            onChange(nextCond)
          }
        }}
      >
        <Trans>Add Filter</Trans>
      </RepeaterButton>
    </div>
  )
}
